import { Select as MuiSelect, type SelectProps as MuiSelectProps } from '@mui/material'
import { SxProps } from '@mui/material/styles'
import { Box } from '@mui/system'
import { useField } from 'formik'
import { at } from 'lodash'
import Typography from 'ui/src/components/Typography'
import TextField from 'ui/src/components/TextField'

export type SelectProps = MuiSelectProps

const Select = (props: SelectProps) => {
  const { MenuProps, ...rest } = props
  const menuSlotProps = MenuProps?.slotProps as
    | {
        paper?: {
          sx?: SxProps
          [key: string]: unknown
        }
        [key: string]: unknown
      }
    | undefined
  const paperSlotProps = menuSlotProps?.paper
  const paperSlotPropsObject =
    paperSlotProps && typeof paperSlotProps !== 'function' ? paperSlotProps : undefined
  const defaultPaperSx = {
    maxHeight: 300,
    boxShadow: '2',
    border: 1,
    borderColor: 'grey.300',
    borderRadius: 1,
    overflow: 'auto',
  }
  const mergedPaperSx = [defaultPaperSx, paperSlotPropsObject?.sx].filter(
    Boolean,
  ) as unknown as SxProps

  return (
    <MuiSelect
      size="small"
      {...rest}
      MenuProps={{
        disableScrollLock: true,
        ...MenuProps,
        slotProps: {
          ...menuSlotProps,
          paper: {
            ...(paperSlotPropsObject as object),
            sx: mergedPaperSx,
          },
        },
      }}
    />
  )
}

type TSelectFieldProps = {
  label?: string | null
  name: string
  sx?: SxProps
  size?: 'medium' | 'small' | undefined
  placeholder?: string
  disabled?: boolean
  children: React.ReactNode
}

export const SelectField = ({
  label,
  name,
  sx,
  size,
  placeholder,
  disabled = false,
  children,
}: TSelectFieldProps) => {
  const [field, meta] = useField(name)

  const renderHelperText = () => {
    const [touched, error] = at(meta, 'touched', 'error')
    if (touched && error) {
      return error
    }
    return ' '
  }
  return (
    <Box
      sx={{
        ...sx,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      {label && (
        <Typography
          variant="body1"
          sx={{
            marginBottom: 1,
          }}
        >
          {label}
        </Typography>
      )}
      <TextField
        select
        helperText={renderHelperText()}
        error={!!(meta.touched && meta.error && true)}
        onChange={field.onChange}
        onBlur={field.onBlur}
        value={field.value || ''}
        name={name}
        fullWidth
        size={size}
        disabled={disabled}
        placeholder={placeholder}
        slotProps={{
          htmlInput: {
            'data-testid': `${name}Input`,
          },
        }}
      >
        {children}
      </TextField>
    </Box>
  )
}

export default Select
